import { __decorate } from 'tslib';
import { Injectable, Pipe, NgModule } from '@angular/core';
import { HttpHeaders, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DecimalPipe, DatePipe } from '@angular/common';
import { Subject, forkJoin, of } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';

/**
 * Options providable to I18nModule
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common/http';
class I18nConfig {
    constructor() {
        /**
         * @Deprecated
         * Relative url from the domain root to the dictionary file
         * describing all available packages.
         */
        this.dictionaryList = null;
        /**
         * The default dictionary to use when no package is defined in
         * the pipe or the translate function.
         */
        this.defaultDictionary = 'main';
        /**
         * Fallback to default locale if translation is missing.
         */
        this.fallbackToDefault = false;
        /**
         * Format used by the i18nNumber pipe
         *
         * For options see: https://angular.io/api/common/DecimalPipe
         *
         * EXAMPLE: 1.0-3,
         */
        this.numberFormat = '1.0-3';
        /**
         * The previous version of the i18n packages used a non-standard "locale"
         * header to send the locale data. This forces a CORS OPTION request, while
         * using the default "Accept-Language" header does not.
         *
         * This option is kept for backward compatibility, and will be removed in the
         * next major version.
         */
        this.useDeprecatedLocaleHeader = false;
        /**
         * When set to false, various debug messages are printed to the console.
         */
        this.debug = false;
    }
}

let I18nService = class I18nService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        /**
         * Key used to store locale in the storage.
         */
        this.storageKey = '@astron/i18n/locale';
        this.queryLocaleParamRegex = /locale=([a-z]{2})(?:-[a-zA-Z]{2})?/;
        /**
         * Supported locale format, what we extract the simplified locale from.
         */
        this.localeFormatRegex = /^([a-z]{2})(?:-[a-zA-Z]{2})?$/;
        /**
         * Array of loaded translations.
         */
        this.dictionaries = {};
        /**
         * List of supported languages.
         *
         * This is loaded from the dictionary file.
         */
        this.supportedLocales = [];
        this.localeChanges$ = new Subject();
        /**
         * Custom config can be provided through the root module,
         * so we merge it with the default. We use this merging method
         * to keep the object reference.
         */
        const defaultConfig = new I18nConfig();
        Object.keys(defaultConfig).forEach(configField => {
            if (!this.config[configField]) {
                this.config[configField] = defaultConfig[configField];
            }
        });
        this.supportedLocales = (this.config.languages || []).map(locale => this.transformLocale(locale));
        this.selectedLocale = this.guessLocale();
        if (!this.config.defaultLocale) {
            console.warn(`[I18nService][constructor] Default locale has not been set in config! This can lead to errors.`);
        }
        if (this.config.dictionaryList) {
            /**
             * Note: this is needed to prevent a maximum call stack error due to circular dependency injection.
             */
            window.setTimeout(() => {
                this.loadDictionaries(this.selectedLocale);
            }, 0);
        }
        this.decimalPipe = new DecimalPipe(this.selectedLocale);
        this.datePipe = new DatePipe(this.selectedLocale);
    }
    get protectedPath() {
        if (!this.config) {
            throw new Error('Missing config.');
        }
        if (!this.config.dictionaryList) {
            throw new Error('Missing dictionaryList property in config.');
        }
        if (typeof this.config.dictionaryList.protectedPath === 'string' && this.config.dictionaryList.protectedPath) {
            return this.config.dictionaryList.protectedPath;
        }
        else if (typeof this.config.dictionaryList.protectedPath === 'function' && this.config.dictionaryList.protectedPath()) {
            return this.config.dictionaryList.protectedPath();
        }
        else {
            throw new Error('The protectedPath parameter in the config is mandatory for this function.');
        }
    }
    /**
     * The currently selected locale.
     *
     * EXAMPLE: 'en'
     */
    get locale() {
        return this.selectedLocale;
    }
    /**
     * List of available locales
     */
    get availableLocales() {
        return [...this.supportedLocales];
    }
    /**
     * Returns an observable what emits the new locale string, when locale changes.
     */
    get localeChanges() {
        return this.localeChanges$.asObservable();
    }
    /**
     * Emits true, when the initial loading (first local change) of the
     * selected locale is finished.
     */
    get ready() {
        return this.localeChanges.pipe(filter(ready => !!ready), take(1), map(() => true));
    }
    /**
     * It's true when the selected locale has values. This can be used in the templates
     */
    get canRender() {
        return !!this.dictionaries[this.selectedLocale];
    }
    /**
     * @deprecated Use `ready` instead.
     */
    getObservable() {
        return this.ready;
    }
    /**
     * @deprecated No real use-case to use this.
     */
    hasDictionaries() {
        return !!this.dictionaries[this.selectedLocale];
    }
    /**
     * Adds a translations package if
     *  - the locale is a supported one
     *  - no translation has been added already
     */
    addTranslation(locale, dictionary) {
        if (!dictionary.packageName) {
            throw new Error(`[I18nService][addDictionaries] Dictionary must have a valid package name.`);
        }
        if (this.config.debug && !this.availableLocales.includes(locale)) {
            console.warn(`[I18nService][constructor][package:${dictionary}] Locale '${locale}' is not listed in the configurations.`);
        }
        this.dictionaries[locale] = this.dictionaries[locale] || {};
        /**
         * Overriding existing translations is not allowed.
         */
        if (this.dictionaries[locale][dictionary.packageName]) {
            throw new Error(`[I18nService][addDictionaries] Cannot overwrite translations for the "${dictionary.packageName}" package with "${locale}" locale!`);
        }
        this.dictionaries[locale][dictionary.packageName] = dictionary.values;
    }
    /**
     * Call this function from your application when the token exists to load dictionaries from server.
     * You should set the protectedPath in config.
     * @param token Bearer token
     */
    loadProtectedDictionariesWithBearer(token) {
        this.token = token;
        this.loadDictionaries(this.selectedLocale, 'PROTECTED');
    }
    /**
     * Loads the default dictionaries for the selected locale.
     * @param locale unique ISO language identifier
     */
    loadDictionaries(locale, type = 'PUBLIC') {
        /**
         * Because of we can add translations through the API also we cannot skip loading of packages anymore
         * if the locale exists, because its possible that only the sub module added it's own translations, but
         * the main app haven't.
         */
        // the list of packages we need to load
        // Note we get here the package file name but we store them by packageName so we need to transform
        const packages = this.config.dictionaryList.packages
            .filter(packageDictionary => !this.dictionaries[locale] || !this.dictionaries[locale][packageDictionary.name]);
        let packageXhrs;
        switch (type) {
            case 'PROTECTED':
                packageXhrs = packages.filter(packageDictironary => packageDictironary.protected)
                    .map(packageDictionary => {
                    const headers = new HttpHeaders();
                    headers.set('Authorization', String('Bearer ' + this.token));
                    return this.http.get(`${this.protectedPath}/${locale}/${packageDictionary.name}.json`, { headers });
                });
                break;
            case 'ALL':
                packageXhrs = packages.filter(packageDictironary => packageDictironary.protected)
                    .map(packageDictionary => {
                    const headers = new HttpHeaders();
                    headers.set('Authorization', String('Bearer ' + this.token));
                    return this.http.get(`${this.protectedPath}/${locale}/${packageDictionary.name}.json`, { headers });
                });
                packageXhrs.push(...packages.filter(packageDictironary => !packageDictironary.protected)
                    .map(packageDictionary => this.http.get(`${this.config.dictionaryList.path}/${locale}/${packageDictionary.name}.json`)));
                break;
            case 'PUBLIC':
                packageXhrs = packages.filter(packageDictironary => !packageDictironary.protected)
                    .map(packageDictionary => this.http.get(`${this.config.dictionaryList.path}/${locale}/${packageDictionary.name}.json`));
                break;
            default:
                throw new Error('Invalid type. The type parameter must be PROTECTED, PUBLIC or ALL');
        }
        const observable = packageXhrs.length ? forkJoin(packageXhrs) : of([]);
        observable.subscribe(dictionaries => {
            dictionaries.map((dic) => this.addTranslation(locale, dic));
            this.selectedLocale = locale;
            localStorage.setItem(this.storageKey, this.selectedLocale);
            this.localeChanges$.next(this.selectedLocale);
        }, error => {
            console.error(`[I18nService][loadDictionaries] Error while loading translations for ${locale} locale.`);
            console.error(error);
            this.dictionaries[locale] = null;
        });
    }
    /**
     * Guess the locale what should be used. The priority order is the following:
     *
     * - `locale` param from query
     * - saved locale from localsStorage
     * - default locale specified in I18nConfig
     * - locale extracted from browser user-agent
     */
    guessLocale() {
        /**
         * Matches the locale from the query params if it's set
         */
        const queryLocaleMatch = this.queryLocaleParamRegex.exec(window.location.search);
        /**
         * We try to get any locale info from the supported modes.
         */
        const rawLocale = queryLocaleMatch
            ? decodeURIComponent(queryLocaleMatch[1])
            : null || localStorage.getItem(this.storageKey) || this.config.defaultLocale || navigator.language;
        const match = this.localeFormatRegex.exec(rawLocale);
        return match ? match[1] : null;
    }
    /**
     * Tries to transform a string to a valid locale representation.
     */
    transformLocale(localeString) {
        const match = this.localeFormatRegex.exec(localeString);
        if (!match) {
            throw new Error(`[I18nService][transformLocale] The "${localeString}" is not a valid locale!`);
        }
        return match[1];
    }
    /**
     * Returns true if the locale is the list of supported locales.
     */
    verifyLocale(locale) {
        const validLocale = !!this.supportedLocales.find(supportedLocale => locale === locale);
        if (!validLocale) {
            console.warn(`[I18nService][verifyLocale] The "${locale}" locale is not in the list of supported languages.`);
        }
        return validLocale;
    }
    /**
     * Changes the selected locale and loads the default dictionaries for it.
     *
     * NOTE: Rejects with null or non 2xx response.
     *
     * @param locale new locale
     */
    changeLocale(locale) {
        return new Promise((resolve, reject) => {
            const transformedLocale = this.transformLocale(locale);
            if (!this.verifyLocale(transformedLocale)) {
                return reject(null);
            }
            if (this.config.dictionaryList) {
                this.loadDictionaries(transformedLocale, 'ALL');
            }
        });
    }
    /**
     * Gives back the translation for the given key.
     * @param key the key to read from the dictionary
     * @param dictionary the selected dictionary, defaults to `config.defaultDictionary`
     * @param parameters key-value pairs to replace in the translation
     * @param locale
     */
    translate(key, dictionary = this.config.defaultDictionary, parameters, locale = this.selectedLocale) {
        var _a, _b;
        let translation = (_b = (_a = this.dictionaries[locale]) === null || _a === void 0 ? void 0 : _a[dictionary]) === null || _b === void 0 ? void 0 : _b[key];
        if (!translation) {
            if (this.config.debug) {
                console.warn(`[I18nService][translate] Cannot read translation for "${key}" in "${dictionary}".`);
            }
            if (locale !== this.config.defaultLocale && this.config.fallbackToDefault) {
                return this.translate(key, dictionary, parameters, this.config.defaultLocale);
            }
            return `${key}:${dictionary}`;
        }
        /**
         * If parameters were provided we replace the placeholders with the properly converted values.
         */
        if (parameters) {
            Object.keys(parameters).map(pKey => {
                let transformedValue = '?ERROR?';
                switch (typeof parameters[pKey]) {
                    case 'string':
                        transformedValue = parameters[pKey];
                        break;
                    case 'number':
                        transformedValue = this.decimalPipe.transform(parameters[pKey], this.config.numberFormat, this.locale);
                        break;
                    case 'object':
                        if (parameters[pKey] instanceof Date) {
                            transformedValue = this.datePipe.transform(parameters[pKey], this.config.dateTimeFormat, undefined, this.locale);
                        }
                        else {
                            if (this.config.debug) {
                                console.warn(`[I18nService][translate] Invalid parameter for "${key}", got object or null but expected a Date.`);
                            }
                        }
                        break;
                    default:
                        console.warn(`[I18nService][translate] "${typeof parameters[pKey]}" is not a valid type for a parameter.`);
                        break;
                }
                translation = translation.replace(new RegExp('{' + pKey + '}', 'g'), transformedValue);
            });
        }
        return translation;
    }
    /**
     * Returns a translation from the specified package with the specified key.
     * @deprecated Use `translate` instead.
     * @param packageName name of the package
     * @param keyName name of the key
     * @param replacement paramters to replace.
     */
    getValue(packageName, keyName, replacement) {
        if (keyName instanceof Object) {
            return keyName[this.selectedLocale];
        }
        return this.translate(keyName, packageName, replacement);
    }
};
I18nService.ɵfac = function I18nService_Factory(t) { return new (t || I18nService)(ɵngcc0.ɵɵinject(ɵngcc1.HttpClient), ɵngcc0.ɵɵinject(I18nConfig)); };
I18nService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: I18nService, factory: function (t) { return I18nService.ɵfac(t); } });
I18nService.ctorParameters = () => [
    { type: HttpClient },
    { type: I18nConfig }
];

let HttpHeaderLocaleExtender = class HttpHeaderLocaleExtender {
    constructor(i18n, config) {
        this.i18n = i18n;
        this.config = config;
    }
    intercept(req, next) {
        const headers = { 'Accept-Language': this.i18n.locale };
        if (this.config.useDeprecatedLocaleHeader) {
            headers['locale'] = this.i18n.locale;
        }
        return next.handle(req.clone({ setHeaders: headers }));
    }
};
HttpHeaderLocaleExtender.ɵfac = function HttpHeaderLocaleExtender_Factory(t) { return new (t || HttpHeaderLocaleExtender)(ɵngcc0.ɵɵinject(I18nService), ɵngcc0.ɵɵinject(I18nConfig)); };
HttpHeaderLocaleExtender.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: HttpHeaderLocaleExtender, factory: function (t) { return HttpHeaderLocaleExtender.ɵfac(t); } });
HttpHeaderLocaleExtender.ctorParameters = () => [
    { type: I18nService },
    { type: I18nConfig }
];

let I18nDatePipe = class I18nDatePipe {
    constructor(i18n) {
        this.i18n = i18n;
        this.datePipe = new DatePipe(this.i18n.locale);
    }
    transform(value, format = this.i18n.config.dateFormat, timezone) {
        return this.datePipe.transform(value, format, timezone, this.i18n.locale);
    }
};
I18nDatePipe.ɵfac = function I18nDatePipe_Factory(t) { return new (t || I18nDatePipe)(ɵngcc0.ɵɵdirectiveInject(I18nService, 16)); };
I18nDatePipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "i18nDate", type: I18nDatePipe, pure: false });
I18nDatePipe.ctorParameters = () => [
    { type: I18nService }
];

let I18nTimePipe = class I18nTimePipe {
    constructor(i18n) {
        this.i18n = i18n;
        this.datePipe = new DatePipe(this.i18n.locale);
    }
    transform(value, format = this.i18n.config.timeFormat, timezone) {
        return this.datePipe.transform(value, format || 'shortTime', timezone, this.i18n.locale);
    }
};
I18nTimePipe.ɵfac = function I18nTimePipe_Factory(t) { return new (t || I18nTimePipe)(ɵngcc0.ɵɵdirectiveInject(I18nService, 16)); };
I18nTimePipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "i18nTime", type: I18nTimePipe, pure: false });
I18nTimePipe.ctorParameters = () => [
    { type: I18nService }
];

let I18nDateTimePipe = class I18nDateTimePipe {
    constructor(i18n) {
        this.i18n = i18n;
        this.datePipe = new DatePipe(this.i18n.locale);
    }
    transform(value, format = this.i18n.config.dateTimeFormat, timezone) {
        return this.datePipe.transform(value, format || 'medium', timezone, this.i18n.locale);
    }
};
I18nDateTimePipe.ɵfac = function I18nDateTimePipe_Factory(t) { return new (t || I18nDateTimePipe)(ɵngcc0.ɵɵdirectiveInject(I18nService, 16)); };
I18nDateTimePipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "i18nDateTime", type: I18nDateTimePipe, pure: false });
I18nDateTimePipe.ctorParameters = () => [
    { type: I18nService }
];

let I18nNumberPipe = class I18nNumberPipe {
    constructor(i18n) {
        this.i18n = i18n;
        this.decimalPipe = new DecimalPipe(this.i18n.locale);
    }
    transform(value, digits = this.i18n.config.numberFormat) {
        return this.decimalPipe.transform(value, digits, this.i18n.locale);
    }
};
I18nNumberPipe.ɵfac = function I18nNumberPipe_Factory(t) { return new (t || I18nNumberPipe)(ɵngcc0.ɵɵdirectiveInject(I18nService, 16)); };
I18nNumberPipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "i18nNumber", type: I18nNumberPipe, pure: false });
I18nNumberPipe.ctorParameters = () => [
    { type: I18nService }
];

let I18nPipe = class I18nPipe {
    constructor(i18nService) {
        this.i18nService = i18nService;
    }
    transform(value, dictionaryName, parameters) {
        if (!dictionaryName) {
            dictionaryName = this.i18nService.config.defaultDictionary;
        }
        return this.i18nService.translate(value, dictionaryName, parameters);
    }
};
I18nPipe.ɵfac = function I18nPipe_Factory(t) { return new (t || I18nPipe)(ɵngcc0.ɵɵdirectiveInject(I18nService, 16)); };
I18nPipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "i18n", type: I18nPipe, pure: false });
I18nPipe.ctorParameters = () => [
    { type: I18nService }
];

var I18nModule_1;
let I18nModule = I18nModule_1 = class I18nModule {
    static forRoot(config) {
        return {
            ngModule: I18nModule_1,
            providers: [
                I18nConfig,
                {
                    provide: I18nConfig,
                    useValue: config,
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: HttpHeaderLocaleExtender,
                    multi: true,
                },
                I18nService,
            ],
        };
    }
    static forChild() {
        return {
            ngModule: I18nModule_1,
        };
    }
};
I18nModule.ɵfac = function I18nModule_Factory(t) { return new (t || I18nModule)(); };
I18nModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: I18nModule });
I18nModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.HttpClient }, { type: I18nConfig }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(HttpHeaderLocaleExtender, [{
        type: Injectable
    }], function () { return [{ type: I18nService }, { type: I18nConfig }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nDatePipe, [{
        type: Pipe,
        args: [{
                name: 'i18nDate',
                pure: false
            }]
    }], function () { return [{ type: I18nService }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nTimePipe, [{
        type: Pipe,
        args: [{
                name: 'i18nTime',
                pure: false
            }]
    }], function () { return [{ type: I18nService }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nDateTimePipe, [{
        type: Pipe,
        args: [{
                name: 'i18nDateTime',
                pure: false
            }]
    }], function () { return [{ type: I18nService }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nNumberPipe, [{
        type: Pipe,
        args: [{
                name: 'i18nNumber',
                pure: false
            }]
    }], function () { return [{ type: I18nService }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nPipe, [{
        type: Pipe,
        args: [{
                name: 'i18n',
                pure: false
            }]
    }], function () { return [{ type: I18nService }]; }, null); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(I18nModule, [{
        type: NgModule,
        args: [{
                declarations: [I18nDatePipe, I18nTimePipe, I18nDateTimePipe, I18nNumberPipe, I18nPipe],
                exports: [I18nDatePipe, I18nTimePipe, I18nDateTimePipe, I18nNumberPipe, I18nPipe]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(I18nModule, { declarations: [I18nDatePipe, I18nTimePipe, I18nDateTimePipe, I18nNumberPipe, I18nPipe], exports: [I18nDatePipe, I18nTimePipe, I18nDateTimePipe, I18nNumberPipe, I18nPipe] }); })();

/**
 * This is the entry point for our library,
 * we re-export here every publicly available module, class, component, etc.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { I18nConfig, I18nDatePipe, I18nDateTimePipe, I18nModule, I18nNumberPipe, I18nPipe, I18nService, I18nTimePipe, HttpHeaderLocaleExtender as ɵa };

